import * as React from 'react';
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Box, Modal, Button, Stack, TextField, Typography,
  Divider, Alert, Chip, Checkbox, FormControlLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '45vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  overflowY: 'auto',
  backgroundColor: '#f5f5f5',
};

export default function ModalParecerAcesso({ open, onClose, proposta, setProposta, anexo, onAddAnexo }) {
  const [valorServico, setValorServico] = useState(proposta?.valorservico || '');
  const [servicoInterno, setServicoInterno] = useState(!!proposta?.servicoInterno);

  const OPCOES_BASE = [1, 2, 3, 4, 5].map((opcao) => ({ opcao, liberada: false, potenciaLiberada: '' }));

  const [inversaoFluxo, setInversaoFluxo] = useState(!!proposta?.inversaoFluxo);
  const [opcoesLiberadas, setOpcoesLiberadas] = useState(
    proposta?.opcoesLiberadas?.length ? proposta.opcoesLiberadas : OPCOES_BASE
  );
  const [opcaoSelecionada, setOpcaoSelecionada] = useState(proposta?.opcaoSelecionada ?? '');
  const [novaPotencia, setNovaPotencia] = useState(proposta?.novaPotencia ?? '');

  // Verificar pelos tipos de anexo
  const temNF = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Nota Fiscal de Projeto'),
    [anexo]
  );
  const temParecer = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Parecer de Acesso'),
    [anexo]
  );
  const temAprovacaoModificacao = React.useMemo(
    () => anexo?.some((a) => a.tipo?.nome === 'Aprovação da Modificação'),
    [anexo]
  );
  const temValor = servicoInterno || Number(valorServico) > 0;
  const temNFDispensada = servicoInterno;

  const opcaoSelecionadaObj = opcoesLiberadas.find((o) => String(o.opcao) === String(opcaoSelecionada));
  const potenciaExcedida = inversaoFluxo
    && opcaoSelecionadaObj
    && novaPotencia !== ''
    && Number(novaPotencia) > Number(opcaoSelecionadaObj.potenciaLiberada || 0);

  React.useEffect(() => {
    if (proposta?.valorservico) setValorServico(proposta.valorservico);
  }, [proposta?.valorservico]);

  const salvarValor = () => {
    setProposta((prev) => ({ ...prev, valorservico: parseFloat(valorServico) || 0 }));
  };

  const handleServicoInterno = (checked) => {
    setServicoInterno(checked);
    setProposta((prev) => ({
      ...prev,
      servicoInterno: checked,
      valorservico: checked ? 0 : parseFloat(valorServico) || 0,
    }));
  };

  const handleInversaoFluxo = (checked) => {
    setInversaoFluxo(checked);
    setProposta((prev) => ({ ...prev, inversaoFluxo: checked }));
  };

  const handleOpcaoLiberadaCheck = (opcaoNum, checked) => {
    const atualizadas = opcoesLiberadas.map((o) => (o.opcao === opcaoNum ? { ...o, liberada: checked } : o));
    setOpcoesLiberadas(atualizadas);
    setProposta((prev) => ({ ...prev, opcoesLiberadas: atualizadas }));
  };

  const handlePotenciaLiberadaChange = (opcaoNum, valor) => {
    const atualizadas = opcoesLiberadas.map((o) => (o.opcao === opcaoNum ? { ...o, potenciaLiberada: valor } : o));
    setOpcoesLiberadas(atualizadas);
    setProposta((prev) => ({ ...prev, opcoesLiberadas: atualizadas }));
  };

  const handlePotenciaLiberadaBlur = () => {
    setProposta((prev) => ({ ...prev, opcoesLiberadas }));
  };

  const handleOpcaoSelecionada = (valor) => {
    setOpcaoSelecionada(valor);
    setProposta((prev) => ({ ...prev, opcaoSelecionada: valor }));
  };

  const handleNovaPotencia = (valor) => {
    setNovaPotencia(valor);
    setProposta((prev) => ({ ...prev, novaPotencia: valor }));
  };

  const StatusItem = ({ label, ok }) => (
    <Stack direction="row" alignItems="center" gap={1} py={0.5}>
      {ok
        ? <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
        : <WarningAmberIcon sx={{ color: '#e65100', fontSize: 20 }} />}
      <Typography variant="body2" color={ok ? '#2e7d32' : '#e65100'}>
        {label}
      </Typography>
      {ok
        ? <Chip label="OK" size="small" color="success" sx={{ ml: 'auto' }} />
        : <Chip label="Pendente" size="small" color="warning" sx={{ ml: 'auto' }} />}
    </Stack>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h6" fontWeight="bold" color="#1565C0" mb={2}>
          Parecer de Acesso — Pendências
        </Typography>

        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Preencha os campos abaixo para avançar para a próxima etapa.
          Todos os três itens são obrigatórios.
        </Alert>

        {/* Serviço executado internamente (engenheiro próprio, não terceirizado) */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={servicoInterno}
                onChange={(e) => handleServicoInterno(e.target.checked)}
                color="primary"
              />
            )}
            label={(
              <Typography variant="body2">
                Serviço executado internamente (dispensa Valor do Serviço e Nota Fiscal de Projeto — o Parecer de Acesso continua obrigatório)
              </Typography>
            )}
          />
        </Box>

        {/* Inversão de Fluxo */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={inversaoFluxo}
                onChange={(e) => handleInversaoFluxo(e.target.checked)}
                color="primary"
              />
            )}
            label={(
              <Typography variant="body2">
                Inversão de Fluxo
              </Typography>
            )}
          />

          {inversaoFluxo && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Marque as opções liberadas pela distribuidora e informe a potência liberada de cada uma.
              </Typography>

              <Stack gap={1} mb={2}>
                {opcoesLiberadas.map((op) => (
                  <Stack key={op.opcao} direction="row" alignItems="center" gap={2}>
                    <FormControlLabel
                      sx={{ minWidth: 110 }}
                      control={(
                        <Checkbox
                          checked={op.liberada}
                          onChange={(e) => handleOpcaoLiberadaCheck(op.opcao, e.target.checked)}
                        />
                      )}
                      label={`Opção ${op.opcao}`}
                    />
                    <TextField
                      size="small"
                      label="Potência Liberada"
                      type="number"
                      value={op.potenciaLiberada}
                      disabled={!op.liberada}
                      onChange={(e) => handlePotenciaLiberadaChange(op.opcao, e.target.value)}
                      onBlur={handlePotenciaLiberadaBlur}
                      sx={{ width: 200, backgroundColor: 'white' }}
                      InputProps={{ endAdornment: <span style={{ marginLeft: 4 }}>kWp</span> }}
                    />
                  </Stack>
                ))}
              </Stack>

              <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <TextField
                  select
                  SelectProps={{ native: true }}
                  size="small"
                  label="Opção Selecionada"
                  value={opcaoSelecionada}
                  onChange={(e) => handleOpcaoSelecionada(e.target.value)}
                  sx={{ width: 220, backgroundColor: 'white' }}
                >
                  <option value="" />
                  {opcoesLiberadas.filter((o) => o.liberada).map((o) => (
                    <option key={o.opcao} value={o.opcao}>
                      {`Opção ${o.opcao} (${o.potenciaLiberada || 0} kWp)`}
                    </option>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label="Nova Potência"
                  type="number"
                  value={novaPotencia}
                  onChange={(e) => handleNovaPotencia(e.target.value)}
                  sx={{ width: 200, backgroundColor: 'white' }}
                  InputProps={{ endAdornment: <span style={{ marginLeft: 4 }}>kWp</span> }}
                />
              </Stack>

              {potenciaExcedida && (
                <Alert severity="warning" sx={{ mt: 2, fontSize: 12 }}>
                  A Nova Potência informada é maior que a Potência Liberada da opção selecionada.
                  Corrija o valor para poder avançar de etapa.
                </Alert>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="#1565C0" mb={1}>
                  Aprovação da Modificação (CEMIG)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Selecione o tipo "Aprovação da Modificação" ao anexar. Visível apenas para supervisor e quem anexou.
                </Typography>
                {temAprovacaoModificacao
                  ? <Chip label="Aprovação já anexada ✓" color="success" size="small" />
                  : (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      onClick={() => onAddAnexo('aprovacao')}
                      sx={{ borderColor: '#1565C0', color: '#1565C0' }}
                    >
                      Adicionar Aprovação da Modificação
                    </Button>
                  )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Status geral */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>Status</Typography>
          <StatusItem label="Valor do Serviço informado" ok={temValor} />
          <Divider />
          <StatusItem label="Nota Fiscal de Projeto anexada" ok={temNF || temNFDispensada} />
          <Divider />
          <StatusItem label="Parecer de Acesso da distribuidora anexado" ok={temParecer} />
          {inversaoFluxo && (
            <>
              <Divider />
              <StatusItem label="Nova Potência dentro do limite liberado" ok={!potenciaExcedida && opcaoSelecionadaObj && novaPotencia !== ''} />
              <Divider />
              <StatusItem label="Aprovação da Modificação (CEMIG) anexada" ok={temAprovacaoModificacao} />
            </>
          )}
        </Box>

        {/* Valor do Serviço — dispensado quando interno */}
        {!servicoInterno && (
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            1. Valor do Serviço de Engenharia
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <TextField
              size="small"
              label="Valor (R$)"
              type="number"
              value={valorServico}
              onChange={(e) => setValorServico(e.target.value)}
              onBlur={salvarValor}
              sx={{ width: 200, backgroundColor: 'white' }}
              InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>R$</span> }}
            />
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={salvarValor}
              disabled={!valorServico || valorServico <= 0}
            >
              Salvar Valor
            </Button>
          </Stack>
        </Box>
        )}

        {/* Nota Fiscal — dispensada quando interno */}
        {!servicoInterno && (
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 2 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            2. Nota Fiscal de Projeto
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Selecione o tipo "Nota Fiscal de Projeto" ao anexar. Visível apenas para supervisor e quem anexou.
          </Typography>
          {temNF
            ? <Chip label="NF já anexada ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('nf')}
                sx={{ borderColor: '#e65100', color: '#e65100' }}
              >
                Adicionar Nota Fiscal
              </Button>
            )}
        </Box>
        )}

        {/* Parecer de Acesso */}
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 2, mb: 3 }}>
          <Typography variant="subtitle2" color="#1565C0" mb={1}>
            3. Parecer de Acesso da Distribuidora
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Selecione o tipo "Parecer de Acesso" ao anexar. Visível apenas para supervisor e quem anexou.
          </Typography>
          {temParecer
            ? <Chip label="Parecer já anexado ✓" color="success" size="small" />
            : (
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => onAddAnexo('parecer')}
                sx={{ borderColor: '#1565C0', color: '#1565C0' }}
              >
                Adicionar Parecer de Acesso
              </Button>
            )}
        </Box>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onClose}
            color={
              temValor && (temNF || temNFDispensada) && temParecer
              && (!inversaoFluxo || (temAprovacaoModificacao && !potenciaExcedida))
                ? 'success' : 'inherit'
            }
          >
            {temValor && (temNF || temNFDispensada) && temParecer
            && (!inversaoFluxo || (temAprovacaoModificacao && !potenciaExcedida))
              ? 'Concluído ✓' : 'Fechar'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

ModalParecerAcesso.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  proposta: PropTypes.object,
  setProposta: PropTypes.func,
  anexo: PropTypes.array,
  onAddAnexo: PropTypes.func,
};